var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var AutoTest_1;
import { Maybe } from '@codegrade/maybe';
import { AssignmentsStore } from '@/store';
import { AutoTestStore } from '@/store/modules/autotest';
import * as utils from '@/utils';
import { immutable } from '@/utils';
import { CacheDecorator } from '@/utils/cache';
import * as autoTest from '.';
let AutoTest = AutoTest_1 = class AutoTest {
    id;
    fixtures;
    globalSetup;
    setupScript;
    gradeCalculation;
    assignmentId;
    resultsAlwaysVisible;
    preferTeacherRevision;
    enableCaching;
    runIds;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    sets;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _cache = new CacheDecorator();
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(id, fixtures, globalSetup, setupScript, gradeCalculation, sets, assignmentId, resultsAlwaysVisible, preferTeacherRevision, enableCaching, runIds) {
        this.id = id;
        this.fixtures = fixtures;
        this.globalSetup = globalSetup;
        this.setupScript = setupScript;
        this.gradeCalculation = gradeCalculation;
        this.assignmentId = assignmentId;
        this.resultsAlwaysVisible = resultsAlwaysVisible;
        this.preferTeacherRevision = preferTeacherRevision;
        this.enableCaching = enableCaching;
        this.runIds = runIds;
        this.sets = Object.freeze(sets.map((s, index) => {
            if (s instanceof autoTest.AutoTestSet) {
                return s;
            }
            return autoTest.AutoTestSet.fromServerData(s, index, id);
        }));
    }
    get steps() {
        return utils.flat1(this.sets.map(set => set.steps));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get runs() {
        return utils.filterMap(this.runIds, AutoTestStore.getRun());
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get run() {
        return Maybe.fromNullable(this.runs[0]);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromServerData(data) {
        return new AutoTest_1(data.id, data.fixtures, data.globalSetup, data.setupScript, data.gradeCalculation, data.sets, data.assignmentId, data.resultsAlwaysVisible, data.preferTeacherRevision, data.enableCaching, Object.freeze(data.runs.map(utils.attr('id'))));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    update(data) {
        return new AutoTest_1(this.id, data.fixtures ?? this.fixtures, data.globalSetup ?? this.globalSetup, data.setupScript ?? this.setupScript, data.gradeCalculation ?? this.gradeCalculation, data.sets ?? this.sets, data.assignmentId ?? this.assignmentId, data.resultsAlwaysVisible ?? this.resultsAlwaysVisible, data.preferTeacherRevision ?? this.preferTeacherRevision, data.enableCaching ?? this.enableCaching, data.runIds ?? this.runIds);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    addRun(run) {
        const newRuns = new Set(this.runIds);
        newRuns.add(run.id);
        return this.update({
            runIds: Object.freeze(Array.from(newRuns)),
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    addSet(newSet) {
        return this.update({
            sets: [...this.sets, newSet],
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    removeSet(toRemove) {
        return this.update({
            sets: this.sets
                .filter(set => !set.equals(toRemove))
                .map((set, setIndex) => set.update({ setIndex })),
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    replaceSet(toReplace) {
        const sets = this.sets.map(set => {
            if (set.equals(toReplace)) {
                return toReplace;
            }
            return set;
        });
        return this.update({ sets });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    updateFixtures(newFixtures) {
        // We take extra care here to keep the original ordering if possible.
        const newLookup = utils.getItem(utils.makeLookupMapping(newFixtures, 'id'));
        const seenIds = new Set();
        const fixtures = this.fixtures.map(oldFixture => newLookup(oldFixture.id)
            .ifJust(() => seenIds.add(oldFixture.id))
            .mapOrDefault(newFixture => newFixture, oldFixture));
        if (seenIds.size !== newFixtures.length) {
            newFixtures.forEach(newFixture => {
                if (!seenIds.has(newFixture.id)) {
                    fixtures.push(newFixture);
                }
            });
        }
        return this.update({ fixtures });
    }
    /**
     * @returns Maybe the assignment of this AutoTest.
     *
     * @deprecated
     */
    get assignment() {
        return AssignmentsStore.getAssignment()(this.assignmentId, {});
    }
    get pointsPossible() {
        return utils.sumMap(this.sets, utils.attr('pointsPossible'));
    }
    get hasCheckpointAfterHiddenStep() {
        return utils
            .findMaybeIndex(set => set.suites.some(suite => suite.steps.some(step => step.hidden)), this.sets)
            .mapOrDefault(firstHiddenSet => {
            // First check if we have a stop points set for a set after a hidden step.
            if (this.sets.slice(firstHiddenSet, -1).some(set => set.stopPoints > 0)) {
                return true;
            }
            // If not try to find a suite with a "check_point" step after a hidden step.
            return this.sets
                .slice(firstHiddenSet)
                .some(set => set.suites.some(suite => utils
                .findMaybeIndex(step => step.hidden, suite.steps)
                .mapOrDefault(firstHiddenStep => suite.steps
                .slice(firstHiddenStep)
                .some(step => step.type === 'check_points'), false)));
        }, false);
    }
};
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTest.prototype, "steps", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTest.prototype, "pointsPossible", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTest.prototype, "hasCheckpointAfterHiddenStep", null);
AutoTest = AutoTest_1 = __decorate([
    immutable
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTest);
export { AutoTest };
